'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createPublicDemoSession,
  deletePublicDemoSession,
  fetchPublicDemosAvailability,
  fetchPublicDemosMeta,
  fetchPublicDemoSessions,
  patchPublicDemoSession,
} from '../action';
import type {
  FetchAvailabilityParams,
  PublicDemoCreateSessionBody,
  PublicDemoPatchSessionBody,
} from '../types';

export function usePublicDemosMeta() {
  return useQuery({
    queryKey: ['public-demos', 'meta'],
    queryFn: async () => {
      const res = await fetchPublicDemosMeta();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
}

export function usePublicDemosAvailability(params: FetchAvailabilityParams) {
  return useQuery({
    queryKey: ['public-demos', 'availability', params],
    queryFn: async () => {
      const res = await fetchPublicDemosAvailability(params);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
}

export function usePublicDemoSessions(limit = 500) {
  return useQuery({
    queryKey: ['public-demos', 'sessions', { limit }],
    queryFn: async () => {
      const res = await fetchPublicDemoSessions(limit);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
}

export function useCreatePublicDemoSession() {
  return useMutation({
    mutationFn: async (body: PublicDemoCreateSessionBody) => {
      const res = await createPublicDemoSession(body);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
}

export function usePatchPublicDemoSession() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      body,
    }: {
      sessionId: string;
      body: PublicDemoPatchSessionBody;
    }) => {
      const res = await patchPublicDemoSession(sessionId, body);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
}

export function useDeletePublicDemoSession() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await deletePublicDemoSession(sessionId);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
}

